import { WorksheetAnswerStatus } from '@packages/models/api';
import { type BadgeVariants } from '@packages/ui/shared';
import { DATE_FORMAT } from '@web/config/constants';
import { formatDate } from '@web/utils/date-format';
import { compareAsc, isValid, parseISO } from 'date-fns';
import { startCase } from 'lodash';

export const getBadgeVariant = (status: string): BadgeVariants => {
    switch (status) {
        // https://subaruofamerica.atlassian.net/browse/TECHSHARE-1769
        case 'Pending':
            return 'yellow';
        case 'NOT_ESCALATED':
            return 'gray';
        case 'ESCALATION_FAILED':
            return 'red';
        case 'TRANSFERRING':
            return 'yellow';
        case 'SENT_TO_TR':
            return 'gray';
        case 'TR_DRAFT':
            return 'gray';
        case 'TR_ISSUED':
            return 'yellow';
        case 'TEMPORARY_REPLY_RECEIVED':
            return 'yellow';
        case 'FINAL_REPLY_RECEIVED':
            return 'green';
        case 'FINAL_REPLY_APPROVED':
            return 'green';
        case 'FINAL_REPLY_REFUSED':
            return 'red';
        default:
            return 'yellow';
    }
};

export const getBadgeVariantTRSharedReply = (status: string): BadgeVariants => {
    switch (status) {
        case 'SHARED':
            return 'green';
        case 'PENDING':
            return 'gray';
        case 'CLOSED':
            return 'green';
        case 'NO_REPLY':
            return 'yellow';
        default:
            return 'yellow';
    }
};

/**
 * @deprecated Use the domain layer's status booleans on `TechlineCaseEntity` (`isDraft`, `isSubmitted`, `isClosed`,
 * `isEscalated`) or the helper functions (`isDraftCase`, `isSubmittedCase`, `isClosedCase`, `isEscalatedCase`) from
 * `@packages/domain/techline/cases`. This map uses stale string values that do not match current API responses
 * (e.g. `'PENDING-TECHLINE'` vs the actual `'PENDING TECHLINE'`). It is retained only because `getBadgeVariantTechline`
 * below still references it.
 */
export const caseStatusMap: any = {
    DRAFT: ['DRAFT'],
    IN_REVIEW: ['IN REVIEW'],
    OPEN: ['OPEN', 'OPEN-ESCALATED'],
    CLOSED: ['CLOSED'],
    PENDING_TECHLINE: ['PENDING-TECHLINE'],
    UPDATE_REQUESTED: ['UPDATE_REQUESTED'],
};

export const getBadgeVariantTechline = (status: string): BadgeVariants => {
    for (const variant in caseStatusMap) {
        if (caseStatusMap[variant].includes(status)) {
            switch (variant) {
                // Add edge case to remove the underscore to stay consistent in the variant styling file
                case 'PENDING_TECHLINE':
                    return 'PENDING';
                case 'UPDATE_REQUESTED':
                    return 'UPDATE';
                case 'IN_REVIEW':
                    return 'OPEN';
                default:
                    return variant as BadgeVariants;
            }
        }
    }

    return 'default';
};

export function getDisplayDate(
    date: Date | undefined | string,
    dateFormat: string = DATE_FORMAT.MM_DD_YEAR,
    defaultValue: string = 'N/A'
): string {
    if (!date) {
        return defaultValue;
    }
    return formatDate(date, dateFormat);
}

// This function is used to display the value with a suffix if it exists, otherwise it will return 'N/A'
export function getDisplayValue(value: any, suffix: string = '', defaultValue: string = 'N/A'): string {
    if (value === undefined || value === null || value === '') {
        return defaultValue;
    }

    return `${value}${suffix}`;
}

/**
 * Formats the time in minutes to a human-readable string.
 *
 * @param minutes - The charge time in minutes (optional).
 * @returns A formatted string, e.g., "1 hour 4 minutes" or "9 minutes".
 */
export function getFormattedTime(minutes?: number): string {
    // Ensure minutes is defined and greater than 0
    if (!minutes || minutes <= 0) {
        return '0 minutes';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
        // Format for hours and minutes
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}${
            remainingMinutes > 0 ? ` ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}` : ''
        }`;
    } else {
        // Format for minutes only
        return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
    }
}

export function getWorksheetBadgeVariant(status: string): BadgeVariants {
    switch (status) {
        case WorksheetAnswerStatus.Draft:
            return 'default';
        case WorksheetAnswerStatus.Submitted:
            return 'green';
    }
    return 'default';
}

/**
 * Formats a given text string to title case, converting all characters to lowercase
 * first and then capitalizing the first letter of each word.
 *
 * @param text - The input text to be formatted.
 * @returns The formatted text in title case.
 * @example
 * formatToTitleCase("SYSTEM TEST FUNCTION - System Test");
 * // Returns "System Test Function - System Test"
 */
export const formatToTitleCase = (text?: string): string => {
    return startCase(text?.toLowerCase());
};

export function compareDatesAsc(a: string, b: string): number {
    const aDate = parseDate(a);
    const bDate = parseDate(b);

    if (!aDate && !bDate) {
        return 0;
    }
    if (!aDate) {
        return 1;
    }
    if (!bDate) {
        return -1;
    }

    return compareAsc(aDate, bDate);
}

export function compareNumbersAsc(a: string | number, b: string | number): number {
    const aNumber = parseNumber(a);
    const bNumber = parseNumber(b);

    const aInvalid = Number.isNaN(aNumber);
    const bInvalid = Number.isNaN(bNumber);

    if (aInvalid && bInvalid) {
        return 0;
    }
    if (aInvalid) {
        return 1;
    }
    if (bInvalid) {
        return -1;
    }

    return aNumber - bNumber;
}

/**
 * Formats a number with commas as thousands separators.
 *
 * @param value - The number to format.
 * @param defaultValue - The string to return if the value is null, undefined, empty, or not a valid number (default: 'N/A').
 * @returns The formatted string, e.g., "36,680".
 */
export function formatNumberWithCommas(value: number | string | undefined, defaultValue: string = 'N/A'): string {
    if (value === null || value === undefined || value === '') {
        return defaultValue;
    }
    const num = typeof value === 'string' ? Number.parseFloat(value) : value;
    if (isNaN(num)) {
        return defaultValue;
    }
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * Returns a formatted string indicating how long ago a given date was, in months or years.
 *
 * @param closedDate - The date to compare against the current date.
 * @returns A string like "1 MONTH AGO", "5 MONTHS AGO", "1 YEAR AGO", or "3 YEARS AGO".
 */
export function getTimeAgoString(closedDate: Date | string): string {
    const date = typeof closedDate === 'string' ? new Date(closedDate) : closedDate;
    const now = new Date();
    const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());

    if (diffMonths < 12) {
        return diffMonths === 1 ? '1 MONTH AGO' : `${diffMonths} MONTHS AGO`;
    } else {
        const years = Math.floor(diffMonths / 12);
        return years === 1 ? '1 YEAR AGO' : `${years} YEARS AGO`;
    }
}

function parseDate(value: string): Date | null {
    if (!value) {
        return null;
    }
    const parsedDate = parseISO(value);
    return isValid(parsedDate) ? parsedDate : null;
}

function parseNumber(value: string | number): number {
    if (!value === null || value === undefined || value === '') {
        return Number.NaN;
    }
    return typeof value === 'string' ? Number.parseFloat(value) : value;
}
